import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import { liberarImagensRemotas, sanitizarHtmlEmail } from '@malgoos/shared';
import { Botao } from '../../componentes/base';
import { recuperarFoco } from '../../nucleo/foco';

const ALTURA_MIN = 80;
const ALTURA_MAX = 640;

/**
 * Fase 2 (W9, F1d): o HTML do e-mail é DADO. O servidor já guardou sanitizado; aqui ele passa de novo pelo sanitizador
 * (defesa em profundidade) e vai para um quadro isolado: `sandbox` sem `allow-scripts` (script nenhum roda, mesmo que
 * escapasse da lista), CSP própria com `default-src 'none'` e imagem só `data:` até o clique em "mostrar imagens" (aí,
 * `https:` também). Links abrem fora, sem referrer. O quadro tem fundo de papel (`Canvas`), como nos leitores de e-mail.
 */
export function montarDocumentoEmail(html: string, mostrarImagens: boolean): string {
  const seguro = sanitizarHtmlEmail(html).html;
  const corpo = mostrarImagens ? liberarImagensRemotas(seguro) : seguro;
  const csp = `default-src 'none'; style-src 'unsafe-inline'; img-src data:${mostrarImagens ? ' https:' : ''}; font-src data:; form-action 'none'; base-uri 'none'`;
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><meta name="referrer" content="no-referrer"><base target="_blank"><style>html{color-scheme:light}body{margin:0;padding:12px;font:14px/1.5 system-ui,sans-serif;background:Canvas;color:CanvasText;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${corpo}</body></html>`;
}

export function EmailHtml({ html, remotas, titulo }: { html: string; remotas: number; titulo: string }) {
  const { t } = useTranslation('conversas');
  const [mostrar, setMostrar] = useState(false);
  const [altura, setAltura] = useState(160);
  const quadro = useRef<HTMLIFrameElement>(null);
  const doc = useMemo(() => montarDocumentoEmail(html, mostrar), [html, mostrar]);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {remotas > 0 && !mostrar && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-borda bg-sup-1 px-3 py-2 text-xs text-tinta-2">
          <ImageOff className="size-4 shrink-0 text-tinta-3" aria-hidden />
          <span className="min-w-0 flex-1 basis-48">{t('w9.imagensBloqueadas', { count: remotas })}</span>
          <Botao
            tamanho="sm"
            variante="fantasma"
            onClick={() => {
              setMostrar(true);
              // Review r30 visual (B2): o aviso some com o botão; o foco vai para o e-mail (o quadro tem título) e não para o body.
              recuperarFoco({ reservas: [() => quadro.current] });
            }}
          >
            {t('w9.mostrarImagens')}
          </Botao>
        </div>
      )}
      <iframe
        ref={quadro}
        title={titulo}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        srcDoc={doc}
        onLoad={() => {
          const h = quadro.current?.contentDocument?.documentElement?.scrollHeight ?? 0;
          if (h) setAltura(Math.min(ALTURA_MAX, Math.max(ALTURA_MIN, h + 2)));
        }}
        className="w-full rounded-lg border border-borda bg-sup-2"
        style={{ height: altura }}
      />
    </div>
  );
}
